const matchesCourseDetail = (pathname) =>
  pathname.startsWith("/courses/") &&
  pathname !== "/courses" &&
  !pathname.includes("/typing-courses");
const matchesLessonPlayer = (pathname) => pathname.startsWith("/learn/lesson/");

export const TOUR_REGISTRY = [
  {
    id: "platform-welcome",
    label: "Welcome Journey",
    routeLabel: "Getting started",
    autoStart: false,
    matches: (pathname) => pathname === "/" || pathname === "/home",
    steps: [
      {
        element: '[data-tour="nav-courses"]',
        title: "Courses are the main exam path",
        description: "Most new learners begin here to discover the exact SSC, RRB, Banking, or state-specific typing track they need.",
        side: "bottom",
      },
    ],
  },
  {
    id: "courses-catalog",
    label: "Courses Tour",
    routeLabel: "Courses",
    autoStart: false,
    matches: (pathname) => pathname === "/courses",
    steps: [
      {
        element: '[data-tour="courses-hero"]',
        title: "Start from the course hub",
        description: "This page is where users browse the full catalog before choosing a path.",
        side: "bottom",
      },
      {
        element: '[data-tour="courses-type-tabs"]',
        title: "Switch between typing and steno",
        description: "Use these tabs to narrow the catalog to the exact practice track you want.",
      },
      {
        element: '[data-tour="courses-search"]',
        title: "Search by exam or keyword",
        description: "Typing part of the exam name is the fastest way to find the right course.",
      },
      {
        element: '[data-tour="courses-categories"]',
        title: "Refine with category filters",
        description: "After search, category chips help users trim the list to SSC, Banking, RRB, and more.",
      },
      {
        element: '[data-tour="course-card-primary"]',
        title: "Open any course card",
        description: "The next step is to open a course and see its related packages or typing-test options.",
        side: "top",
        continueAction: {
          label: "Open Course",
          action: {
            type: "navigate-storage",
            key: "typogram_tour_first_course_route",
          },
        },
      },
    ],
  },
  {
    id: "course-detail",
    label: "Course Detail Tour",
    routeLabel: "Course detail",
    autoStart: false,
    matches: matchesCourseDetail,
    steps: [
      {
        element: '[data-tour="course-detail-header"]',
        title: "This page explains the course track",
        description: "Users land here after choosing a course from the catalog.",
      },
      {
        element: '[data-tour="course-related-grid"]',
        title: "Choose the exact typing package",
        description: "Each card here is a concrete course or pass the learner can start or buy.",
      },
      {
        element: '[data-tour="course-journey-cta"]',
        title: "Add this course to your active courses",
        description: "This step enrolls the learner into the guided free course and sends them to the typing test setup flow.",
        side: "top",
        continueAction: {
          label: "Add to Active Courses",
          action: {
            type: "click",
            selector: '[data-tour="course-journey-cta"]',
          },
        },
      },
    ],
  },
  {
    id: "free-course-join",
    label: "Free Course Join",
    routeLabel: "Telegram join",
    autoStart: false,
    matches: matchesCourseDetail,
    steps: [
      {
        element: '[data-tour="telegram-modal"]',
        title: "Free courses ask for a Telegram join first",
        description: "This quick step helps users join the Typogram community before continuing with a free course.",
      },
      {
        element: '[data-tour="telegram-join-link"]',
        title: "Open the Telegram community",
        description: "The journey will open the join link first, then continue to course activation.",
        side: "top",
      },
    ],
  },
  {
    id: "free-course-continue",
    label: "Free Course Continue",
    routeLabel: "Telegram continue",
    autoStart: false,
    matches: matchesCourseDetail,
    steps: [
      {
        element: '[data-tour="telegram-continue"]',
        title: "Continue after joining",
        description: "This finishes the free course activation and sends the learner to Start Test.",
        side: "top",
      },
    ],
  },
  {
    id: "checkout",
    label: "Checkout Tour",
    routeLabel: "Checkout",
    autoStart: false,
    matches: (pathname) => pathname === "/checkout",
    steps: [
      {
        element: '[data-tour="checkout-summary"]',
        title: "Review the order first",
        description: "Use the order summary to confirm validity, taxes, discounts, and the exact access this purchase unlocks.",
        side: "left",
      },
      {
        element: '[data-tour="checkout-coupon"]',
        title: "Apply offers here",
        description: "If this purchase supports coupons, apply the code here before opening the payment flow.",
      },
      {
        element: '[data-tour="checkout-payment"]',
        title: "Complete the payment securely",
        description: "This section opens the real payment flow. The tour only explains the secure payment area and never auto-triggers checkout.",
      },
    ],
  },
  {
    id: "tests-setup",
    label: "Start Test Tour",
    routeLabel: "Start test",
    autoStart: false,
    matches: (pathname) => pathname === "/start-test",
    steps: [
      {
        element: '[data-tour="test-hero"]',
        title: "This is the guided start flow",
        description: "Users move through course, test, and settings in one clear funnel.",
      },
      {
        element: '[data-tour="test-course-select"]',
        title: "Step 1: choose a course",
        description: "Pick the purchased course or demo source before anything else.",
      },
      {
        element: '[data-tour="test-passage-select"]',
        title: "Step 2: choose the test or passage",
        description: "After selecting a course, the user chooses the exact test passage to attempt.",
      },
      {
        element: '[data-tour="test-settings-panel"]',
        title: "Step 3: review settings",
        description: "Language, font, and exam preferences should look correct before launching.",
      },
      {
        element: '[data-tour="test-start-button"]',
        title: "Start the typing test here",
        description: "Once the course and passage are ready, this button opens the full exam screen.",
        side: "top",
        continueAction: {
          label: "Start Typing",
          action: {
            type: "click",
            selector: '[data-tour="test-start-button"]',
          },
        },
      },
    ],
  },
  {
    id: "tests-active",
    label: "Active Test Tour",
    routeLabel: "Active test",
    autoStart: false,
    matches: (pathname) => pathname === "/typing-display-test",
    steps: [
      {
        element: '[data-tour="active-test-shell"]',
        title: "You are now inside the test screen",
        description: "This layout is distraction-free so users can focus on the live attempt.",
      },
      {
        element: '[data-tour="active-test-passage"]',
        title: "Read the reference passage here",
        description: "Users compare against this passage unless paper mode is enabled.",
      },
      {
        element: '[data-tour="active-test-input"]',
        title: "Type in this answer box",
        description: "Typing begins here, and timer, analytics, and scoring all depend on this box.",
      },
      {
        element: '[data-tour="active-test-actions"]',
        title: "Submit or cancel from these controls",
        description: "The actions stay simple so users always know how to finish or exit the test.",
      },
      {
        element: '[data-tour="active-test-summary"]',
        title: "Keep an eye on your test summary",
        description: "This side panel helps users confirm identity and the current test context.",
      },
    ],
  },
  {
    id: "practice-direct",
    label: "Practice Tour",
    routeLabel: "Practice",
    autoStart: true,
    matches: (pathname) => pathname === "/typing-display",
    steps: [
      {
        element: '[data-tour="practice-brand"]',
        title: "Direct practice opens as a minimal workspace",
        description: "This page is built for instant repetition, so the branding stays light and the typing surface gets most of the attention.",
      },
      {
        element: '[data-tour="practice-surface"]',
        title: "Typing starts in the main practice surface",
        description: "The active typing mode appears here, whether it is words, sentence, free typing, or cards.",
      },
      {
        element: '[data-tour="practice-theme"]',
        title: "Change the visual theme here",
        description: "This selector changes the full typing surface theme, so users can pick the colors that feel easiest on their eyes.",
        side: "top",
      },
      {
        element: '[data-tour="practice-focus-mode"]',
        title: "Focus mode removes extra distractions",
        description: "Use this when the learner wants a cleaner, calmer screen with less visual noise around the typing flow.",
        side: "top",
      },
      {
        element: '[data-tour="practice-sound-mode"]',
        title: "Typing sounds can be turned on here",
        description: "This lets users hear a key sound while practicing, which some learners use for rhythm and tactile feedback.",
        side: "top",
      },
      {
        element: '[data-tour="practice-words-card-mode"]',
        title: "Word cards turn practice into flash-style prompts",
        description: "This mode is useful when the learner wants chunked prompts instead of the normal flowing typing surface.",
        side: "top",
      },
      {
        element: '[data-tour="practice-free-typing-mode"]',
        title: "Free typing opens an open text practice area",
        description: "This is the no-prompt mode, useful when users want to type anything freely instead of following generated text.",
        side: "top",
      },
      {
        element: '[data-tour="practice-trainer-mode"]',
        title: "Trainer mode focuses on keyboard guidance",
        description: "This helps users practice with a keyboard-focused training view instead of a normal typing test.",
        side: "top",
      },
      {
        element: '[data-tour="practice-music-mode"]',
        title: "Music mode adds a more immersive session",
        description: "Learners can switch into a music-driven practice experience when they want a different practice mood.",
        side: "top",
      },
      {
        element: '[data-tour="practice-fullscreen"]',
        title: "Fullscreen keeps the entire page dedicated to practice",
        description: "Use this to remove browser clutter and give the learner a larger practice surface.",
        side: "top",
      },
      {
        element: '[data-tour="practice-lyrics-mode"]',
        title: "Lyrics mode turns practice into lyric-based typing",
        description: "This mode is useful when users want a more playful practice format built around song lyrics.",
        side: "top",
      },
      {
        element: '[data-tour="practice-font-options"]',
        title: "Font tools let users tune readability",
        description: "This button reveals typography controls so the learner can make the prompt easier to read.",
        side: "top",
      },
      {
        element: '[data-tour="practice-controls"]',
        title: "The rest of the bottom bar adapts to the current mode",
        description: "Some extra controls appear only in certain modes, like sound style, font family, font size, or word-versus-sentence typing, so the bar evolves with the learner's current practice setup.",
        side: "top",
      },
    ],
  },
  {
    id: "learn-hub",
    label: "Lesson Hub Tour",
    routeLabel: "Learn hub",
    autoStart: true,
    matches: (pathname) => pathname === "/learn",
    steps: [
      {
        element: '[data-tour="learn-hero"]',
        title: "This is the full typing curriculum",
        description: "The lesson hub organizes your typing journey from beginner drills to advanced practice in one place.",
      },
      {
        element: '[data-tour="learn-progress"]',
        title: "Track progress, stars, and XP here",
        description: "This summary helps learners see completion momentum before choosing the next lesson.",
      },
      {
        element: '[data-tour="learn-section-toggle"]',
        title: "Open one unit at a time",
        description: "Each section expands into a lesson deck so the curriculum stays easy to scan on mobile and desktop.",
      },
      {
        element: '[data-tour="learn-path"]',
        title: "The lesson path reveals the full sequence",
        description: "Once a learner opens a unit, the lesson deck underneath shows what is done, what is next, and what is still locked.",
        side: "top",
      },
    ],
  },
  {
    id: "lesson-player",
    label: "Lesson Player Tour",
    routeLabel: "Lesson player",
    autoStart: true,
    matches: matchesLessonPlayer,
    steps: [
      {
        element: '[data-tour="lesson-player-topbar"]',
        title: "Return to the lesson map anytime",
        description: "The back button stays visible so learners never get trapped inside a lesson.",
      },
      {
        element: '[data-tour="lesson-player-stage"]',
        title: "This stage adapts to the current lesson phase",
        description: "Depending on the lesson, this area shows instructions, the live typing passage, or the final results state.",
      },
      {
        element: '[data-tour="lesson-player-controls"]',
        title: "Use the controls to move, restart, or tune sound",
        description: "Lesson navigation and practice controls stay close to the typing flow so learners can retry without leaving the page.",
        side: "top",
      },
    ],
  },
  {
    id: "community-hub",
    label: "Community Tour",
    routeLabel: "Community",
    autoStart: true,
    matches: (pathname) => pathname === "/community",
    steps: [
      {
        element: '[data-tour="community-header"]',
        title: "Community is organized around support formats",
        description: "This page groups accountability, study rooms, mental health support, and focus-building spaces in one hub.",
      },
      {
        element: '[data-tour="community-grid"]',
        title: "Browse the active spaces here",
        description: "Each card represents a different support format, so learners can choose what matches their current need.",
      },
    ],
  },
  {
    id: "steno-practice",
    label: "Steno Practice Tour",
    routeLabel: "Steno practice",
    autoStart: true,
    matches: (pathname) => pathname === "/steno/practice",
    steps: [
      {
        element: '[data-tour="steno-practice-topbar"]',
        title: "Keep the session controls within reach",
        description: "The top bar shows the passage title, phase, timer, and quick actions like fullscreen or exit.",
      },
      {
        element: '[data-tour="steno-practice-controls"]',
        title: "Adjust dictation settings before you begin",
        description: "This area controls playback speed, voice, fluctuation, and audio volume for the live dictation session.",
      },
      {
        element: '[data-tour="steno-practice-setup"]',
        title: "Review the setup before starting transcription",
        description: "The setup card explains the session phases and helps learners choose the right typing route before the test begins.",
      },
      {
        element: '[data-tour="steno-practice-start"]',
        title: "Start the steno session from here",
        description: "When the settings look right, begin the dictation and move into the live transcription flow.",
        side: "top",
      },
    ],
  },
  {
    id: "leaderboard-tour",
    label: "Leaderboard Tour",
    routeLabel: "Leaderboard",
    autoStart: true,
    matches: (pathname) => pathname === "/leaderboard",
    steps: [
      {
        element: '[data-tour="leaderboard-header"]',
        title: "Leaderboards turn practice into visible progress",
        description: "This page helps learners compare their speed, XP, and streak consistency against the wider community.",
      },
      {
        element: '[data-tour="leaderboard-tabs"]',
        title: "Switch ranking modes here",
        description: "Tabs let users compare pure speed, consistency streaks, or total XP progression.",
      },
      {
        element: '[data-tour="leaderboard-table"]',
        title: "The full rankings appear below",
        description: "This table is where users track rank changes and see what metric each board rewards.",
      },
    ],
  },
  {
    id: "profile-tour",
    label: "Profile Tour",
    routeLabel: "Profile",
    autoStart: true,
    matches: (pathname) => pathname === "/profile",
    steps: [
      {
        element: '[data-tour="profile-header"]',
        title: "Profile brings account context into one place",
        description: "This page combines identity, streaks, progress, active courses, and support resources instead of scattering them.",
      },
      {
        element: '[data-tour="profile-progress"]',
        title: "Check your momentum here",
        description: "XP, level, and streaks give learners a fast signal of how consistently they are practicing.",
      },
      {
        element: '[data-tour="profile-courses"]',
        title: "See what you can practice right now",
        description: "The active courses section shows the learner's unlocked typing paths and available practice access.",
      },
      {
        element: '[data-tour="profile-help"]',
        title: "Use quick tutorials when you get stuck",
        description: "These help videos keep support close to the profile instead of forcing users to search elsewhere.",
      },
    ],
  },
  {
    id: "blog-hub",
    label: "Blog Tour",
    routeLabel: "Blog",
    autoStart: true,
    matches: (pathname) => pathname === "/blog",
    steps: [
      {
        element: '[data-tour="blog-hero"]',
        title: "The blog is the long-form learning library",
        description: "This hub collects typing guides, exam prep advice, and deeper articles that support daily practice.",
      },
      {
        element: '[data-tour="blog-search"]',
        title: "Search by exam, skill, or problem",
        description: "Learners can quickly find posts about speed, accuracy, SSC, RRB, or any typing issue they want to solve.",
      },
      {
        element: '[data-tour="blog-filters"]',
        title: "Filter the content by category or view",
        description: "These filters help narrow the library without losing context across tags, categories, and layout modes.",
      },
      {
        element: '[data-tour="blog-posts"]',
        title: "Open the article list from here",
        description: "The main article feed is where users keep exploring once they narrow the topic they care about.",
      },
    ],
  },
  {
    id: "home-discovery",
    label: "Home Discovery Tour",
    routeLabel: "Home",
    autoStart: false,
    matches: (pathname) => pathname === "/" || pathname === "/home",
    steps: [
      {
        element: '[data-tour="home-discovery-hero"]',
        title: "The home page introduces Typogram's core promise",
        description: "This hero section quickly explains that the platform supports exam typing, guided practice, and improvement workflows.",
      },
      {
        element: '[data-tour="home-discovery-actions"]',
        title: "Start, try the demo, or launch the guided flow here",
        description: "The top actions help first-time visitors choose whether they want direct practice or a structured walkthrough.",
      },
      {
        element: '[data-tour="home-discovery-workflows"]',
        title: "Workflow cards explain the main product paths",
        description: "These sections show how courses, tests, and other core flows fit together before a user commits to one.",
      },
    ],
  },
];

export const TOUR_IDS = TOUR_REGISTRY.map((tour) => tour.id);

export const TOUR_JOURNEYS = [
  {
    id: "new-user-onboarding",
    label: "New User Journey",
    startPath: "/courses",
    autoStart: false,
    autoStartMatches: (pathname) =>
      pathname === "/" ||
      pathname === "/home" ||
      pathname === "/courses",
    stages: [
      {
        tourId: "platform-welcome",
        advanceOn: "done",
        nextPath: "/courses",
      },
      {
        tourId: "courses-catalog",
        advanceOn: "done",
        nextPath: "/courses",
        onDoneAction: {
          type: "navigate-storage",
          key: "typogram_tour_first_course_route",
        },
      },
      {
        tourId: "course-detail",
        advanceOn: "done",
        onDoneAction: {
          type: "click",
          selector: '[data-tour="course-free-cta"]',
        },
      },
      {
        tourId: "tests-setup",
        advanceOn: "done",
        onDoneAction: {
          type: "click",
          selector: '[data-tour="test-start-button"]',
        },
      },
      {
        tourId: "tests-active",
        advanceOn: "done",
      },
    ],
  },
];

export function getTourById(tourId) {
  return TOUR_REGISTRY.find((tour) => tour.id === tourId) || null;
}

export function getTourForPath(pathname) {
  return TOUR_REGISTRY.find((tour) => tour.matches(pathname)) || null;
}

export function getJourneyById(journeyId) {
  return TOUR_JOURNEYS.find((journey) => journey.id === journeyId) || null;
}
